import { cva, type VariantProps } from 'class-variance-authority';
import styles from './chipNavigator.module.scss';
import { buttonClass } from '../Button';
import Select from '../inputs/Select';

type ChipLink = {
  id: string;
  label: string;
  href: string;
};

const DEFAULT_LINKS = [
  {
    id: 'all',
    label: 'All',
    href: '/resources'
  },
  {
    id: 'articles',
    label: 'Articles',
    href: '/resources/articles'
  },
  {
    id: 'interviews',
    label: 'Interviews',
    href: '/resources/interviews'
  },
  {
    id: 'case-studies',
    label: 'Case studies',
    href: '/resources/case-studies'
  },
  {
    id: 'tools-calculators',
    label: 'Tools & calculators',
    href: '/resources/tools-calculators'
  },
  {
    id: 'product-spotlight',
    label: 'Product spotlight',
    href: '/resources/spotlight'
  }
] as ChipLink[];

const ChipNavigatorClass = cva(styles.chipNavigator, {
  variants: {},
  defaultVariants: {}
});

type RenderLinkComponent = (props: {
  className: string;
  href: string;
  label: string;
}) => React.ReactNode;

type ChipNavigatorProps = VariantProps<typeof ChipNavigatorClass> & {
  className?: string;
  links?: ChipLink[];
  selectedLinkId?: string;
  onMobileSelect?: (linkId: string) => void;
  renderLinkComponent?: RenderLinkComponent;
};

const ChipLink = ({
  label,
  href,
  selected,
  renderLinkComponent = ({ className, href, label }) => (
    <a href={href} className={className}>
      {label}
    </a>
  )
}: ChipLink & {
  selected?: boolean;
  renderLinkComponent?: RenderLinkComponent;
}) => {
  return renderLinkComponent({
    className: buttonClass({
      className: selected ? styles.selected : styles.link,
      size: 'small',
      colorScheme: 'secondary'
    }),
    href,
    label
  });
};

export default function ChipNavigator({
  className,
  links = DEFAULT_LINKS,
  selectedLinkId,
  onMobileSelect,
  renderLinkComponent
}: ChipNavigatorProps) {
  return (
    <>
      <div className={ChipNavigatorClass({ className })}>
        {links.map((link) => (
          <ChipLink
            key={link.id}
            {...link}
            selected={link.id === selectedLinkId}
            renderLinkComponent={renderLinkComponent}
          />
        ))}
      </div>
      <div className={styles.dropdown}>
        <Select
          triggerProps={{
            variant: 'chip'
          }}
          onValueChange={(value) => {
            onMobileSelect?.(links.find((link) => link.id === value)!.href);
          }}
          options={links.map((link) => ({
            label: link.label,
            value: link.id
          }))}
          defaultValue={selectedLinkId || links[0]?.id}
        />
      </div>
    </>
  );
}
